// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 현장 주소(slug) 검사 — **웹에서는 여기 한 곳뿐이어야 한다**
//
//  ★ 지금 저장소에 같은 규칙의 사본이 **넷**이다:
//      ① db/001_schema.sql:193  (진짜. CHECK 제약)
//      ② SCREEN-DESIGN.html:686 (목업의 checkSlug)
//      ③ apps/web/src/App.tsx:52 `/^[a-z][a-z0-9-]{1,39}$/`  ← ①보다 **느슨하다**
//      ④ 그리고 이 화면이 또 하나를 만들 뻔했다.
//    ③ 은 `da--jin` · `dangjin-` 을 통과시킨다. 라우팅용이라 지금은 무해하지만
//    (그런 slug 는 애초에 만들어질 수 없다), **만들기 화면이 그걸 복사하면**
//    그날 「화면은 ✓ · DB 는 거부」가 재현된다. 사용자는 폼을 다 채운 뒤에 그걸 본다.
//
//  ⚠️ 통합할 때 이 파일을 `apps/web/src/slug.ts` 로 옮기고 `App.tsx:52` 가 여기서
//     import 하게 해라. 그러면 사본이 넷에서 **둘(DB · 웹 하나씩)** 로 준다.
//     그게 이번에 할 수 있는 최선이다 — DB CHECK 는 SQL 이라 공유할 수 없다.
//
//  ⚠️ **예약어를 이 파일에 적지 마라.** `GET /api/catalog` 가 DB 의 `reserved_slug`
//     80개를 그대로 준다(004:70 이 이미 열어 뒀다). 여기 다시 적으면 다섯 벌이 된다.
// ─────────────────────────────────────────────────────────────────────────

/** DB CHECK(001:193)와 같은 정규식. 서버도 같은 문자열을 `/api/catalog` 로 내려 준다. */
export const SLUG_RE = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;
export const SLUG_MIN = 2;
export const SLUG_MAX = 40;

export type SlugState = 'empty' | 'bad' | 'reserved' | 'taken' | 'ok';

export interface SlugCheck {
  state: SlugState;
  /** 사용자가 읽을 한 문장. 「무엇이 틀렸나」가 아니라 **「그래서 뭘 하면 되나」**로 쓴다. */
  msg: string;
}

/**
 * 타이핑 중에 부른다. **서버에 묻지 않는 것만** 여기서 본다.
 *
 * @param reserved `GET /api/catalog` 가 준 예약어. 아직 안 왔으면 빈 배열을 넘긴다
 *                 (그 상태에서는 예약어 검사를 건너뛴다 — 서버가 최종 판정을 한다).
 * @param taken    내 조직에서 이미 쓰는 주소. `GET /api/projects` 목록에서 뽑는다.
 *                 ⚠️ **지워진 현장은 여기 없다.** `project_select` 가 active 만 주는데
 *                    (003:93) 삭제된 현장도 slug 를 영구 점유한다(001:198). 그래서
 *                    「눌러서 실패하는 경우」가 정확히 하나 남는다 → 서버의 409 가 그것이다.
 */
export function checkSlug(raw: string, reserved: readonly string[], taken: readonly string[]): SlugCheck {
  const v = raw.trim();

  if (!v) return { state: 'empty', msg: '주소를 넣어 주세요.' };

  // ★ 순서가 중요하다. 한글·대문자를 **먼저** 잡아야 사람이 고칠 수 있는 문장이 나온다.
  //   「영문 소문자·숫자·하이픈만 됩니다」를 대문자 입력에 보여 주면, 사용자는 자기가
  //   이미 영문을 썼다고 생각해서 무엇이 틀렸는지 못 찾는다.
  if (/[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(v)) {
    return {
      state: 'bad',
      msg: '한글은 쓸 수 없습니다. 맥과 윈도우가 같은 글자를 다른 값으로 보내서 주소가 갈라집니다.',
    };
  }
  if (/[A-Z]/.test(v)) {
    return { state: 'bad', msg: `대문자는 쓸 수 없습니다 — ${v.toLowerCase()} 로 넣어 주세요.` };
  }
  if (/[^a-z0-9-]/.test(v)) {
    return { state: 'bad', msg: '영문 소문자 · 숫자 · 하이픈(-) 만 됩니다.' };
  }
  if (v.length < SLUG_MIN || v.length > SLUG_MAX) {
    return { state: 'bad', msg: `${SLUG_MIN}자에서 ${SLUG_MAX}자 사이여야 합니다.` };
  }
  if (!/^[a-z]/.test(v)) {
    return { state: 'bad', msg: '영문자로 시작해야 합니다.' };
  }
  if (/^[0-9]+$/.test(v)) {
    return { state: 'bad', msg: '숫자만으로는 안 됩니다.' };
  }
  if (!SLUG_RE.test(v)) {
    // 여기까지 왔는데 정규식이 막는 것은 하이픈 배치뿐이다.
    return { state: 'bad', msg: '하이픈이 연달아 오거나 맨 끝에 올 수 없습니다.' };
  }
  if (reserved.includes(v)) {
    return { state: 'reserved', msg: `「${v}」 은 시스템이 쓰는 주소라 쓸 수 없습니다.` };
  }
  if (taken.includes(v)) {
    return {
      state: 'taken',
      msg: `「${v}」 은 이미 쓰이고 있습니다. 지운 현장이 쓰던 주소일 수도 있습니다 — 그 주소는 되돌려 받을 수 없습니다.`,
    };
  }
  return { state: 'ok', msg: '쓸 수 있는 주소입니다.' };
}
